import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import { BattleResult, BattleResultType } from '../types/BattleState';
import { GameMode } from '../types/GameMode';
import {
  RESULT_LAYOUT,
  RESULT_COLORS,
  RESULT_TEXT,
  getResultButtons,
} from './resultConfig';
import { updateClearedStages, updateBossDefeatedWith, unlockHiddenMonster, loadGameProgress } from '../utils/gameProgressManager';
import {
  shouldTriggerMococoChallenge,
  shouldShowBossFirstClearHint,
  createMococoChallengeBattleData,
} from '../utils/challengeBattleManager';
import { checkCampaignAchievements, checkHiddenCharacterAchievements, unlockAchievements } from '../utils/achievementManager';
import { showAchievementToast } from '../utils/achievementToast';
import { PartyMemberState } from '../battle/partyBattle';
import { addBattleHistoryEntry } from '../utils/battleHistoryManager';
import { buildShareText, buildShareUrl } from '../utils/shareUtils';
import { getChallengeStage, getNextStageNumber } from '../constants/challengeConfig';
import { playBgm, stopBgm, playSe } from '../utils/audioManager';
import { showInterstitialIfNeeded } from '../utils/adMobManager';
import { AudioKey } from '../constants/audioKeys';
import { BackgroundImageKey } from '../constants/imageKeys';
import { calculateStatsDiff } from '../constants/monsterStats';
import { SocketClient } from '../network/SocketClient';

/** ResultSceneに渡されるデータ */
export interface ResultSceneData {
  /** バトル結果 */
  battleResult?: BattleResult;
  /** ゲームモード */
  mode?: GameMode;
  /** ステージ番号（CHALLENGEモード） */
  stageNumber?: number;
  /** クリア済みステージ数（CHALLENGEモード） */
  clearedStages?: number;
  /** プレイヤーモンスターID */
  monsterId?: string;
  /** 再プレイかどうか（CHALLENGEモード） */
  isReplay?: boolean;
  /** Socket接続（ネットワーク対戦用。リザルト画面を離れる際に切断する） */
  socketClient?: SocketClient;
  /** ボスステージフラグ */
  isBossStage?: boolean;
  /** ボス戦で使用したプレイヤーパーティ */
  playerParty?: PartyMemberState[];
  /** 隠しキャラ挑戦戦フラグ */
  isHiddenChallenge?: boolean;
  /** 挑戦戦の対象となる隠しキャラID */
  challengeTargetId?: string;
}

/**
 * リザルト画面シーン
 *
 * 勝敗表示、残りHP表示、遷移ボタンを表示する。
 */
export class ResultScene extends BaseScene {
  private gameMode?: GameMode;
  private stageNumber?: number;
  private clearedStages?: number;
  private monsterId?: string;
  private resultType?: BattleResultType;
  private isReplay?: boolean;
  private socketClient?: SocketClient;
  private isBossStage = false;
  private isHiddenChallenge = false;
  private challengeTargetId?: string;
  private pendingMococoChallengeMonsterId?: string;
  private showBossHint = false;

  constructor() {
    super(SceneKey.RESULT);
  }

  create(data?: ResultSceneData): void {
    showInterstitialIfNeeded();
    this.isBossStage = data?.isBossStage ?? false;
    const isBossVictory = this.isBossStage && data?.battleResult?.resultType === BattleResultType.PLAYER1_WIN;

    // ボス撃破はCONGRATULATIONS背景、通常はRESULT背景
    const bgKey = isBossVictory ? BackgroundImageKey.BOSS_RESULT : BackgroundImageKey.RESULT;
    if (this.textures.exists(bgKey)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, bgKey).setOrigin(0.5);
    } else if (this.textures.exists(BackgroundImageKey.RESULT)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.RESULT).setOrigin(0.5);
    }
    const battleResult = data?.battleResult;
    this.gameMode = data?.mode;
    this.stageNumber = data?.stageNumber;
    this.clearedStages = data?.clearedStages;
    this.monsterId = data?.monsterId;
    this.resultType = battleResult?.resultType;
    this.isReplay = data?.isReplay;
    this.socketClient = data?.socketClient;
    this.isHiddenChallenge = data?.isHiddenChallenge ?? false;
    this.challengeTargetId = data?.challengeTargetId;
    this.pendingMococoChallengeMonsterId = undefined;

    // 隠しキャラ挑戦戦の勝利時に解放・実績付与
    if (this.isHiddenChallenge && this.challengeTargetId && this.resultType === BattleResultType.PLAYER1_WIN) {
      unlockHiddenMonster(this.challengeTargetId);
      const unlockedIds = loadGameProgress().unlockedHiddenMonsters;
      const newAchievements = unlockAchievements(checkHiddenCharacterAchievements(unlockedIds));
      if (newAchievements.length > 0) showAchievementToast(this, newAchievements);
    }

    // CHALLENGE勝利時に進捗更新
    if (
      this.gameMode === GameMode.CHALLENGE &&
      this.stageNumber &&
      this.resultType === BattleResultType.PLAYER1_WIN
    ) {
      let latestProgress = updateClearedStages(this.stageNumber);
      this.clearedStages = latestProgress.clearedStages;

      // ボス撃破時：使用した3キャラをフラグ保存
      if (isBossVictory && data?.playerParty) {
        this.showBossHint = shouldShowBossFirstClearHint(latestProgress);
        const usedIds = data.playerParty.map((m) => m.monsterId);
        latestProgress = updateBossDefeatedWith(usedIds);
      }

      const newAchievements = unlockAchievements(checkCampaignAchievements(latestProgress));
      if (newAchievements.length > 0) showAchievementToast(this, newAchievements);

      // ボス撃破後のモココ挑戦戦トリガー確認
      if (isBossVictory && shouldTriggerMococoChallenge(latestProgress) && data?.playerParty?.[0]) {
        this.pendingMococoChallengeMonsterId = data.playerParty[0].monsterId;
      }
    }

    // バトル履歴に記録（disconnectによる結果、観戦モード、サバイバルモード、大会モード、隠しキャラ挑戦戦は除く）
    if (battleResult && this.gameMode && !this.isHiddenChallenge && this.gameMode !== GameMode.SPECTATOR && this.gameMode !== GameMode.SURVIVAL && this.gameMode !== GameMode.TOURNAMENT_ROUND_ROBIN && this.gameMode !== GameMode.TOURNAMENT_ELIMINATION && battleResult.reason !== 'disconnect') {
      addBattleHistoryEntry({
        resultType: battleResult.resultType,
        gameMode: this.gameMode,
        playerMonsterId: this.monsterId ?? battleResult.finalState.player1.monsterId,
        opponentMonsterId: battleResult.finalState.player2.monsterId,
        turnCount: battleResult.finalState.currentTurn,
        playerFinalHp: battleResult.finalState.player1.currentHp,
        opponentFinalHp: battleResult.finalState.player2.currentHp,
      });
    }

    // 結果に応じたBGM・SEを再生
    if (this.resultType === BattleResultType.PLAYER1_WIN) {
      playBgm(this.sound, AudioKey.BGM_RESULT_VICTORY);
      playSe(this.sound, AudioKey.SE_VICTORY);
    } else if (this.resultType === BattleResultType.PLAYER2_WIN) {
      playBgm(this.sound, AudioKey.BGM_RESULT_DEFEAT);
      playSe(this.sound, AudioKey.SE_DEFEAT);
    } else {
      stopBgm();
    }

    this.createResultDisplay(battleResult?.resultType, battleResult?.reason);
    this.createHpDisplay(battleResult);
    this.createGrowthDisplay();
    this.createButtons();
    this.createShareButton(battleResult);
    this.createHiddenChallengeUI();
  }

  /** 勝敗テキストを表示 */
  private createResultDisplay(resultType?: BattleResultType, reason?: string): void {
    const isBossVictory = this.isBossStage && resultType === BattleResultType.PLAYER1_WIN;

    if (isBossVictory) {
      this.add
        .text(GAME_WIDTH / 2, RESULT_LAYOUT.resultTextY - 30, 'CONGRATULATIONS', {
          fontSize: '52px',
          color: '#ffdd44',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 6,
        })
        .setOrigin(0.5);
      this.add
        .text(GAME_WIDTH / 2, RESULT_LAYOUT.resultTextY + 40, 'エレボスを倒した！', {
          fontSize: '28px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5);
      return;
    }

    const text = resultType ? RESULT_TEXT[resultType] : '---';
    const color = this.getResultColor(resultType);

    this.add
      .text(GAME_WIDTH / 2, RESULT_LAYOUT.resultTextY, text, {
        fontSize: '48px',
        color,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    if (reason === 'disconnect') {
      const disconnectMessage = resultType === BattleResultType.PLAYER1_WIN
        ? '相手の切断により勝利'
        : '通信が切断されました';
      this.add
        .text(GAME_WIDTH / 2, RESULT_LAYOUT.resultTextY + 60, disconnectMessage, {
          fontSize: '20px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5);
    }
  }

  /** 残りHP表示 */
  private createHpDisplay(battleResult?: BattleResult): void {
    if (!battleResult) return;

    const { finalState } = battleResult;
    const playerHp = finalState.player1.currentHp;
    const enemyHp = finalState.player2.currentHp;

    this.add
      .text(
        GAME_WIDTH / 2,
        RESULT_LAYOUT.hpDisplayY,
        `あなた HP: ${playerHp}　　相手 HP: ${enemyHp}`,
        {
          fontSize: '20px',
          color: RESULT_COLORS.textColor,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        }
      )
      .setOrigin(0.5);
  }

  /** CHALLENGE勝利時にパラメータ成長差分を表示 */
  private createGrowthDisplay(): void {
    if (this.isReplay) return;
    if (
      this.gameMode !== GameMode.CHALLENGE ||
      this.resultType !== BattleResultType.PLAYER1_WIN ||
      !this.monsterId ||
      !this.clearedStages
    ) return;

    const diff = calculateStatsDiff(this.monsterId, this.clearedStages);
    if (!diff) return;

    const startY = RESULT_LAYOUT.growthDisplayY;
    const lineH = 25;

    this.add.text(GAME_WIDTH / 2, startY, 'キャラ成長！', {
      fontSize: '18px',
      color: '#ffdd44',
      fontFamily: "'Noto Sans JP', Arial, sans-serif",
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const lines = [
      { label: 'HP',    d: diff.hp },
      { label: '腕力',  d: diff.strength },
      { label: '特殊',  d: diff.special },
      { label: '素早さ', d: diff.speed },
      { label: '丈夫さ', d: diff.toughness },
    ];

    lines.forEach((line, i) => {
      const gainStr = line.d.gain > 0 ? `+${line.d.gain}` : `${line.d.gain}`;
      this.add.text(
        GAME_WIDTH / 2,
        startY + lineH + i * lineH,
        `${line.label}: ${line.d.before} → ${line.d.after}（${gainStr}）`,
        {
          fontSize: '15px',
          color: line.d.gain > 0 ? '#88ff88' : '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        }
      ).setOrigin(0.5);
    });
  }

  /** ボタンを生成 */
  private createButtons(): void {
    const isHiddenChallengeWin = this.isHiddenChallenge && this.resultType === BattleResultType.PLAYER1_WIN;
    const buttons = getResultButtons(this.gameMode, this.gameMode === GameMode.CHALLENGE ? {
      stageNumber: this.stageNumber,
      resultType: this.resultType,
      isReplay: this.isReplay,
    } : undefined, isHiddenChallengeWin);

    buttons.forEach((buttonConfig, index) => {
      const y = RESULT_LAYOUT.buttonStartY + index * RESULT_LAYOUT.buttonSpacing;

      const text = this.add
        .text(GAME_WIDTH / 2, y, buttonConfig.label, {
          fontSize: '28px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => {
        text.setColor('#ffffff');
        text.setScale(1.1);
      });

      text.on('pointerout', () => {
        text.setColor('#ffffff');
        text.setScale(1.0);
      });

      text.on('pointerdown', () => {
        this.handleButtonClick(buttonConfig.targetScene, buttonConfig.label);
      });
    });
  }

  /** ボタンクリック処理 */
  private handleButtonClick(targetScene: SceneKey, buttonLabel: string): void {
    if (this.gameMode === GameMode.CHALLENGE) {
      this.handleChallengeButtonClick(targetScene, buttonLabel);
      return;
    }
    if (this.socketClient) {
      this.socketClient.disconnect();
    }
    if (targetScene === SceneKey.CHARACTER_SELECT && this.gameMode === GameMode.FREE_CPU) {
      this.transitionTo(targetScene, {
        mode: GameMode.FREE_CPU,
        step: 'player',
      });
    } else if (targetScene === SceneKey.CHARACTER_SELECT && this.gameMode === GameMode.FREE_CPU_3V3) {
      this.transitionTo(targetScene, {
        mode: GameMode.FREE_CPU_3V3,
        step: 'player',
      });
    } else {
      this.transitionTo(targetScene);
    }
  }

  /** CHALLENGE用ボタンクリック処理 */
  private handleChallengeButtonClick(targetScene: SceneKey, buttonLabel: string): void {
    if (targetScene === SceneKey.TITLE) {
      this.transitionTo(SceneKey.TITLE);
      return;
    }

    if (targetScene === SceneKey.STAGE_SELECT) {
      this.transitionTo(SceneKey.STAGE_SELECT);
      return;
    }

    if (buttonLabel === '次へ' && this.stageNumber) {
      // 次のステージへ
      const nextStageNumber = this.stageNumber + 1;
      const nextStage = getChallengeStage(nextStageNumber);
      if (nextStage) {
        this.transitionTo(SceneKey.BATTLE, {
          monsterId: this.monsterId,
          mode: GameMode.CHALLENGE,
          stageNumber: nextStageNumber,
          clearedStages: this.clearedStages,
        });
      }
    } else if (buttonLabel === 'リトライ' && this.stageNumber) {
      // 同じステージへ
      this.transitionTo(SceneKey.BATTLE, {
        monsterId: this.monsterId,
        mode: GameMode.CHALLENGE,
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        isReplay: this.isReplay,
      });
    }
  }

  /** SNSシェアボタンを生成 */
  private createShareButton(battleResult?: BattleResult): void {
    const shareText = buildShareText({
      resultType: battleResult?.resultType ?? BattleResultType.DRAW,
      monsterId: this.monsterId,
      turnCount: battleResult?.finalState.currentTurn ?? 0,
    });
    const shareUrl = buildShareUrl(shareText);

    const button = this.add
      .text(GAME_WIDTH / 2, RESULT_LAYOUT.shareButtonY, 'Xでシェア', {
        fontSize: '22px',
        color: '#1da1f2',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    button.on('pointerover', () => button.setScale(1.1));
    button.on('pointerout', () => button.setScale(1.0));
    button.on('pointerdown', () => {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    });
  }

  /** 隠しキャラ挑戦戦・モココ挑戦戦の特別UIを表示 */
  private createHiddenChallengeUI(): void {
    const cy = GAME_HEIGHT - 80;

    // 初回ボス撃破ヒント（モココ解放条件ヒント）
    if (this.showBossHint && !this.pendingMococoChallengeMonsterId) {
      this.add.text(GAME_WIDTH / 2, cy, '「全ての魂格でこの影を打ち倒せ…」', {
        fontSize: '16px',
        color: '#aaaaff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'italic',
      }).setOrigin(0.5);
      return;
    }

    // 挑戦戦結果の表示
    if (this.isHiddenChallenge && this.challengeTargetId) {
      const isWin = this.resultType === BattleResultType.PLAYER1_WIN;
      const monster = this.challengeTargetId === 'alice' ? 'アリス' : 'モココ';
      const msg = isWin
        ? `${monster}を解放しました！`
        : `また挑戦しよう。次の大会${this.challengeTargetId === 'alice' ? '優勝' : 'ボス撃破'}で再挑戦可。`;
      this.add.text(GAME_WIDTH / 2, cy, msg, {
        fontSize: '18px',
        color: isWin ? '#ffdd44' : '#aaaaaa',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: isWin ? 'bold' : 'normal',
      }).setOrigin(0.5);
    }

    // ボス撃破後のモココ挑戦戦ポップアップ
    if (this.pendingMococoChallengeMonsterId) {
      this.showMococoChallengePopup(this.pendingMococoChallengeMonsterId);
    }
  }

  private showMococoChallengePopup(monsterId: string): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const DEPTH = 20;
    const FONT = "'Noto Sans JP', Arial, sans-serif";

    // 半透明オーバーレイ（背後のボタンへの入力を遮断）
    this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setDepth(DEPTH).setInteractive();

    // パネル
    this.add.rectangle(cx, cy, 580, 300, 0x1a0a1e).setDepth(DEPTH + 1);
    this.add.rectangle(cx, cy, 576, 296, 0x000000, 0)
      .setStrokeStyle(2, 0xff99cc).setDepth(DEPTH + 1);

    // キャラ画像（左寄せ）
    const img = this.add.image(cx - 160, cy, 'monster_mococo_battle')
      .setDepth(DEPTH + 2);
    const scale = Math.min(200 / img.width, 240 / img.height);
    img.setScale(scale);

    // テキスト（右エリア）
    this.add.text(cx + 60, cy - 60, '謎のマスコットが現れた！', {
      fontSize: '20px', color: '#ff99cc', fontFamily: FONT, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 2);

    // 挑戦するボタン
    const btnBg = this.add.rectangle(cx + 60, cy + 50, 190, 46, 0xcc2266)
      .setDepth(DEPTH + 2).setInteractive({ useHandCursor: true });
    this.add.text(cx + 60, cy + 50, '挑戦する！', {
      fontSize: '22px', color: '#ffffff', fontFamily: FONT, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 3);

    btnBg.on('pointerover', () => btnBg.setFillStyle(0xff3388));
    btnBg.on('pointerout',  () => btnBg.setFillStyle(0xcc2266));
    btnBg.on('pointerdown', () => {
      const challengeData = createMococoChallengeBattleData(monsterId);
      this.transitionTo(SceneKey.BATTLE, { ...challengeData, mode: GameMode.FREE_CPU });
    });
  }

  /** 結果タイプに応じた色文字列を返す */
  private getResultColor(resultType?: BattleResultType): string {
    if (!resultType) return RESULT_COLORS.textColor;

    const colorMap: Record<BattleResultType, number> = {
      [BattleResultType.PLAYER1_WIN]: RESULT_COLORS.winColor,
      [BattleResultType.PLAYER2_WIN]: RESULT_COLORS.loseColor,
      [BattleResultType.DRAW]: RESULT_COLORS.drawColor,
    };

    const numColor = colorMap[resultType];
    return `#${numColor.toString(16).padStart(6, '0')}`;
  }
}
